import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  AdminUserInfo,
  clearAdminSession,
  getAdminToken,
  hasPermission,
  PERMS,
  validateAdminSession,
} from '../../utils/adminAuth';
import './AdminPanelPage.css';

const API_BASE_URL = process.env.REACT_APP_API_URL || '/api';

interface FeatureCard {
  title: string;
  description: string;
  path: string;
  viewPerm: string;
}

const FEATURES: FeatureCard[] = [
  {
    title: 'Reports, Calendar',
    description: 'Month calendar of who worked each day, by time or by role',
    path: '/admin/reports-calendar',
    viewPerm: PERMS.reportsCalendarView,
  },
  {
    title: 'Emergency Contacts',
    description: 'View all currently checked-in volunteers and their emergency contact information for emergency situations',
    path: '/admin/emergency-contacts',
    viewPerm: PERMS.emergencyView,
  },
  {
    title: 'Manage Volunteers',
    description: 'Add/remove/edit volunteer records. Manually enter or import.',
    path: '/admin/manage-volunteers',
    viewPerm: PERMS.manageVolunteersView,
  },
  {
    title: 'Liability Waivers',
    description: 'View and manage liability waiver status, track expiration dates, and identify expired waivers',
    path: '/admin/liability-waivers',
    viewPerm: PERMS.liabilityView,
  },
  {
    title: 'Reports, Directory',
    description: 'Export alphabetized list of active volunteers, their contact information and emergency contact information',
    path: '/admin/reports-directory',
    viewPerm: PERMS.reportsDirectoryView,
  },
  {
    title: 'Reports, Time',
    description: 'Export hours by date, task, volunteer, etc. (CSV, charts)',
    path: '/admin/reports-time',
    viewPerm: PERMS.reportsTimeView,
  },
  {
    title: 'Notifications',
    description: 'See who was auto-checked-out or didn\'t respond to reminders',
    path: '/admin/notifications',
    viewPerm: PERMS.notificationsView,
  },
  {
    title: 'Settings',
    description: 'Manage admin and supervisor accounts and supervisor permissions',
    path: '/admin/settings',
    viewPerm: PERMS.settingsView,
  },
  {
    title: 'Activity Log',
    description: 'Append-only history of admin and supervisor changes',
    path: '/admin/activity-log',
    viewPerm: PERMS.activityLogView,
  },
  {
    title: 'Feedback',
    description: 'Read volunteer suggestions and comments submitted from the app',
    path: '/admin/feedback',
    viewPerm: PERMS.feedbackView,
  },
];

const AdminPanelPage: React.FC = () => {
  const navigate = useNavigate();
  const [isValidating, setIsValidating] = useState(true);
  const [user, setUser] = useState<AdminUserInfo | null>(null);

  useEffect(() => {
    const validateToken = async () => {
      const session = await validateAdminSession();
      if (!session) {
        navigate('/admin-login');
        return;
      }
      if (session.mustResetPassword) {
        navigate('/admin/change-password');
        return;
      }
      setUser(session);
      setIsValidating(false);
    };

    validateToken();
  }, [navigate]);

  const handleLogout = async () => {
    const token = getAdminToken();
    try {
      await fetch(`${API_BASE_URL}/admin/logout`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
    } catch {
      // Logout locally even if server request fails
    }
    clearAdminSession();
    navigate('/');
  };

  if (isValidating) {
    return (
      <div className="admin-panel-container">
        <div className="admin-panel-card">
          <div className="loading-spinner"></div>
          <p>Validating session...</p>
        </div>
      </div>
    );
  }

  const visibleFeatures = FEATURES.filter((f) => hasPermission(f.viewPerm, user));

  return (
    <div className="admin-panel-container">
      <div className="admin-panel-card">
        <div className="admin-panel-header">
          <div className="logo">
            <img src="/VolunteerHeart.png" alt="FabMo Volunteer" className="logo-image" />
          </div>
          <h1 className="admin-panel-title">FabMo Volunteer Management System</h1>
          <p className="admin-panel-subtitle">
            {user?.displayName || user?.username} · {user?.role === 'supervisor' ? 'Supervisor' : user?.role === 'breakglass' ? 'Break-glass Admin' : 'Admin'}
          </p>
        </div>

        <div className="admin-panel-content">
          <div className="admin-actions">
            <div className="action-grid">
              {visibleFeatures.map((feature) => (
                <div
                  key={feature.path}
                  className="action-card"
                  onClick={() => navigate(feature.path)}
                >
                  <h3>{feature.title}</h3>
                  <p>{feature.description}</p>
                </div>
              ))}
              {user?.role === 'breakglass' && (
                <div
                  className="action-card"
                  onClick={() => navigate('/admin/reset-liability-waivers')}
                >
                  <h3>Reset all liability waivers</h3>
                  <p>Clear every volunteer&apos;s liability waiver so they have to accept it again. No emails are sent.</p>
                </div>
              )}
              {user?.role === 'breakglass' && (
                <div
                  className="action-card"
                  onClick={() => navigate('/admin/fix-midnight-hours')}
                >
                  <h3>Fix forgotten sign-outs</h3>
                  <p>Shorten old sessions that ran until midnight to exactly 3 hours.</p>
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="admin-panel-footer">
          <button onClick={handleLogout} className="logout-button">
            Logout
          </button>
        </div>
      </div>
    </div>
  );
};

export default AdminPanelPage;
